import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import AppSyncClient from 'lixani-lib-graphql';
import 'settings/dayjs';
import i18n from './i18n';
import moment from 'moment';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from 'redux';
import { I18nextProvider, useTranslation } from 'react-i18next';
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  createMigrate,
} from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider, useSelector } from 'react-redux';
import { transitions, positions, Provider as AlertProvider } from 'react-alert';
import { useDispatch } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import AlertTemplate from './AlertTemplate';
import './index.css';
import Config from './Config';
import App from './scenes/App';
import ErrorBoundary from './ErrorBoundary';
import rootReducer from './store/reducer';
import { configureStore } from '@reduxjs/toolkit';
import workCalendarReducer from './scenes/WorkDemo/workCalendarSlice';
import workSearchParamsReducer from './scenes/WorkDemo/workSearchParamsSlice';
import bulletinBoardReducer from './scenes/BulletinBoard/BulletinBoardSlice';
import { sensorDataReducer, renologAdminReducer } from 'store/renolog';
import { floorplanPlacementsReducer } from 'store/floorplanPlacements';
import activityLogReducer from 'store/activityLog/activityLogSlice';

import projectFormsReducer from './scenes/ProjectForms/ProjectFormSlice';
import defectListReducer from './scenes/DefectList/store/DefectListSlice';
import firestopDocumentationReducer from './store/firestopDocumentation/FirestopDocumentationSlice';

import { permissionSlice } from './scenes/Permissions/store/slices/permissionsSlice';
import resourcesReducer from 'store/resources/resourcesSlice';
import projectsReducer from 'store/projects/projectsSlice';
import projectConfigurationsSliceReducer from 'store/project-configurations/projectConfigurationsSlice';
import businessConfigurationsSliceReducer from 'store/business-configurations/businessConfigurationsSlice';

import mutationQueryResolver from './LixaniAPI/mutationQueryResolver';

import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { useTheme } from './components/theme';

import * as serviceWorkerRegistration from './registerServiceWorker';
import { projectPermissionsSlice } from 'scenes/Permissions/store/slices/projectPermissionsSlice';
import { permissionsDialogSlice } from 'scenes/Permissions/store/slices/permissionsDialogSlice';
import { projectGallerySlice } from 'scenes/ProjectGallery/store/slices/projectGallerySlice';
import { recordsSlice } from 'store/records/recordsSlice';
import { settingsSlice } from 'scenes/Settings/store/slices/settingsSlice';
import { invoicesSlice } from 'store/invoices/invoicesSlice';
import { orientationSlice } from 'store/orientation/orientationSlice';
import interactionsSliceReducer from 'store/interactions/interactionsSlice';

import { assignmentsSlice } from 'store/assignments/assignmentsSlice';
import shadowDataSlice from 'store/shadowData/shadowDataSlice';
import { notificationSnackbarSlice } from 'store/notificationSnackbar/notificationSnackbar.slice';
import breadcrumbsSliceReducer from 'store/breadcrumbs/breadcrumbsSlice';
import { loadUserSettings } from 'store/user/thunks/loadUserSettings.thunk';
import {
  selectUserLoaded,
  selectUserSettingsLoaded,
} from 'store/user/selectors.js/user.selectors';
import userSlice from 'store/user/userSlice';
import { loadUser } from 'store/user/thunks/loadUser.thunk';
import { projectJoinSlice } from 'store/projects/slices/projectJoinSlice';
import projectUsersSlice from 'store/projectUsers/projectUsersSlice';
import { reportsSlice } from 'store/reports/reportsSlice';
import { expenseTrackingSlice } from 'scenes/ExpenseTracking/store/expenseTrackingSlice';
import filepermissionReactionUsersSlice from 'store/filepermissionReactionUsers/filepermissionReactionUsersSlice';
import { filepermissionsSlice } from 'store/filepermissions/filepermissionsSlice';
import { projectBankSlice } from 'scenes/ProjectBank/store/slices/projectBank.slice';
import { translationsSlice } from 'store/translations/translationsSlice';
import { userProfileSlice } from 'store/userProfile/userProfileSlice';
import { approvalsSlice } from 'store/approvals/approvalsSlice';
import { timelineSlice } from 'scenes/Timelines/store/timelineSlice';
import { manualContactsSlice } from 'store/manualContacts/manualContacts.slice';
import { imageSelectorSlice } from 'components/ImageSelectorDialog/store/imageSelectorSlice';
import { SnackbarProvider } from 'notistack';
import { employerAllowancesSlice } from 'store/employerAllowances/employerAllowancesSlice';
import { projectResourcesSlice } from 'scenes/ProjectResources/store/projectResourcesSlice';
import recordAllowancesSlice from 'store/recordAllowances/recordAllowancesSlice';
import cardsSlice from 'store/cards/cardsSlice';
import customerSlice from 'store/customer/customerSlice';
import paymentsSlice from 'store/payments/paymentsSlice';
import devicesSlice from 'store/devices/devicesSlice';
import notificationsSlice from 'store/notifications/notificationsSlice';
import filingsSlice from 'scenes/Filings/FilingsSlice';
import kanbanReducer from 'scenes/Kanban/store/slices/kanban.slice';
import { mediaTagsSlice } from 'store/mediaTags/mediaTags.slice';
import { floorplansSlice } from 'store/floorplans/slices/floorplansSlice';
import { saleInvoicesSlice } from 'store/saleInvoices/saleInvoicesSlice';
import { formEditorSlice } from 'scenes/Settings/FormEditor/store/slices/formEditorSlice';
import { pdfSlice } from 'scenes/Settings/FormEditor/store/slices/pdfSlice';
import { customFormsSlice } from 'scenes/ProjectForms/components/CustomForms/store/customFormsSlice';
import { filledCustomFormsSlice } from 'scenes/ProjectForms/components/CustomForms/store/filledCustomFormSlice';
import { chatbotSlice } from 'scenes/Settings/Chatbot/store/slices/chatbotSlice';
import { organizationChartSlice } from 'store/organizationCharts/organizationChartsSlice';
import { drawingToolSlice } from 'store/lixaniPaint/slices/drawingToolSlice';
import { areaPlanningSlice } from 'scenes/AreaPlanning/store/slices/areaPlanningSlice';
import { chatSlice } from 'components/ChatDialog/store/slices/chatSlice';
import { chatbotAnalyticsSlice } from 'scenes/Settings/ChatbotAnalytics/store/slices/chatbotAnalyticsSlice';
import { nerobottiSlice } from 'scenes/Nerobotti/store/nerobottiSlice';
import { videoChatSlice } from 'components/ChatDialog/store/slices/videoChatSlice';
import { lixaniPaintSlice } from 'store/lixaniPaint/slices/lixaniPaintSlice';
import { projectEmailSlice } from 'store/projectEmail/projectEmailSlice';
import { contactsSlice } from 'store/contacts/contactsSlice';
import { notificationPermissionDialogSlice } from 'store/notificationPermissionDialog/notificationPermissionDialog.slice';
import { loadBusinessConfiguration } from 'store/business-configurations/thunks/loadBusinessConfiguration.thunk';
import { logProjectOpened } from 'store/activityLog/thunks/logProjectOpened.thunk';
import { logTimestampsOpened } from 'store/activityLog/thunks/logTimestampsOpened.thunk';
import Icon from '@mdi/react';
import { mdiRobotDeadOutline } from '@mdi/js';
import Typography from '@material-ui/core/Typography';

// if (Config.SENTRY_DSN) {
//   Sentry.init({
//     dsn: Config.SENTRY_DSN,
//     release: Config.BUILD_TS,
//     integrations: function (integrations) {
//       return integrations.filter(function (integration) {
//         if (!process.env.production) {
//           // Disables breadcrumbs unless in production mode
//           return integration.name !== 'Breadcrumbs';
//         }
//       });
//     },
//   });
// }

const graphqlErrorHandler = (graphQLErrors) => {
  console.log(graphQLErrors);
  // for (let error of graphQLErrors) {
  //   const { message, locations, path } = error;
  //   Sentry.addBreadcrumb({
  //     category: 'graphql',
  //     message: `GraphQL error: ${message}`,
  //     data: {
  //       locations: JSON.stringify(locations),
  //       path: JSON.stringify(path),
  //     },
  //     level: 'error',
  //   });
  // }
  // Sentry.captureException(new Error('GraphQL error'));
};

// todo: Configure sentry user
//Sentry.configureScope((scope) => {
//  scope.setUser({"id": "123-123"});
//  scope.setUser({"email": "john.doe@example.com"});
//});

const migrations = {
  [Config.BUILD_TS]: (state) => {
    // initial state will be returned.
    return undefined;
  },
};

const rootPersistConfig = {
  key: 'root',
  storage: storage,
  whitelist: [''],
  version: Config.BUILD_TS,
  migrate: createMigrate(migrations, { debug: false }),
};

const projectUsersPersisConfig = {
  key: 'projectUsers',
  storage: storage,
  whitelist: ['users', 'userPersonalResources'],
  version: Config.BUILD_TS,
  migrate: createMigrate(migrations, { debug: false }),
};

const projectsPersistConfig = {
  key: 'projects',
  storage: storage,
  whitelist: ['projects'],
  version: Config.BUILD_TS,
  migrate: createMigrate(migrations, { debug: false }),
};

const resourcesPersistConfig = {
  key: 'resources',
  storage: storage,
  whitelist: ['loggedInUserResources'],
  version: Config.BUILD_TS,
  migrate: createMigrate(migrations, { debug: false }),
};

const appReducer = combineReducers({
  root: rootReducer,
  workCalendar: workCalendarReducer,
  workSearchParams_demo: workSearchParamsReducer,
  bulletinBoard: bulletinBoardReducer,
  projectForms: projectFormsReducer,
  defectList: defectListReducer,
  firestopDocumentation: firestopDocumentationReducer,
  projectConfigurations: projectConfigurationsSliceReducer,
  businessConfigurations: businessConfigurationsSliceReducer,
  permissions: permissionSlice.reducer,
  projectPermissions: projectPermissionsSlice.reducer,
  permissionsDialog: permissionsDialogSlice.reducer,
  resources: persistReducer(resourcesPersistConfig, resourcesReducer),
  expenseTracking: expenseTrackingSlice.reducer,
  projects: persistReducer(projectsPersistConfig, projectsReducer),
  projectJoin: projectJoinSlice.reducer,
  projectGallery: projectGallerySlice.reducer,
  assignments: assignmentsSlice.reducer,
  shadowData: shadowDataSlice.reducer,
  invoices: invoicesSlice.reducer,
  saleInvoices: saleInvoicesSlice.reducer,
  orientation: orientationSlice.reducer,
  records: recordsSlice.reducer,
  settings: settingsSlice.reducer,
  notificationSnackbar: notificationSnackbarSlice.reducer,
  breadcrumbs: breadcrumbsSliceReducer,
  user: userSlice,
  projectUsers: persistReducer(projectUsersPersisConfig, projectUsersSlice),
  reports: reportsSlice.reducer,
  filePermissions: filepermissionsSlice.reducer,
  filepermissionReactionUsers: filepermissionReactionUsersSlice,
  interactions: interactionsSliceReducer,
  projectBank: projectBankSlice.reducer,
  translations: translationsSlice.reducer,
  userProfile: userProfileSlice.reducer,
  approvals: approvalsSlice.reducer,
  timelines: timelineSlice.reducer,
  manualContacts: manualContactsSlice.reducer,
  mediaTags: mediaTagsSlice.reducer,
  floorplans: floorplansSlice.reducer,
  imageSelector: imageSelectorSlice.reducer,
  employerAllowances: employerAllowancesSlice.reducer,
  projectResources: projectResourcesSlice.reducer,
  recordAllowances: recordAllowancesSlice,
  cards: cardsSlice,
  customers: customerSlice,
  payments: paymentsSlice,
  devices: devicesSlice,
  notifications: notificationsSlice,
  filings: filingsSlice,
  formEditorSlice: formEditorSlice.reducer,
  pdfSlice: pdfSlice.reducer,
  customFormsSlice: customFormsSlice.reducer,
  filledCustomFormsSlice: filledCustomFormsSlice.reducer,
  chatbotSlice: chatbotSlice.reducer,
  chatbotAnalyticsSlice: chatbotAnalyticsSlice.reducer,
  nerobottiSlice: nerobottiSlice.reducer,
  organizationCharts: organizationChartSlice.reducer,
  drawingTool: drawingToolSlice.reducer,
  areaPlanning: areaPlanningSlice.reducer,
  chatSlice: chatSlice.reducer,
  videoChatSlice: videoChatSlice.reducer,
  lixaniPaint: lixaniPaintSlice.reducer,
  projectEmailSlice: projectEmailSlice.reducer,
  contacts: contactsSlice.reducer,
  notificationPermissionDialog: notificationPermissionDialogSlice.reducer,
  renologSensorData: sensorDataReducer,
  renologAdmin: renologAdminReducer,
  floorplanPlacements: floorplanPlacementsReducer,
  activityLog: activityLogReducer,
  kanban: kanbanReducer,
});

const persistedReducer = persistReducer(rootPersistConfig, appReducer);

const businessConfigurationMiddleware = (storeApi) => (next) => (action) => {
  const result = next(action);

  if (action.type === 'SET_PROJECT') {
    const businessId = action.project?.business_id;

    if (businessId) {
      storeApi.dispatch(loadBusinessConfiguration(businessId));
    }
  }

  if (action.type === 'project/setSelectedProjectId') {
    const selectedProjectId = action.payload;

    if (selectedProjectId) {
      const state = storeApi.getState();
      const selectedProject =
        state.projects?.projects?.entities?.[selectedProjectId];
      const businessId = selectedProject?.business_id;

      if (businessId && state.root?.project?.id !== selectedProjectId) {
        storeApi.dispatch(loadBusinessConfiguration(businessId));
      }
    }
  }

  return result;
};

// Logs project-navigation activity from the URL on project/setSelectedProjectId
// (dispatched by every project route, incl. the work calendar via initWorkCalendar):
//  - VIEW_PROJECT once per project entry, covering the dashboard and any
//    deep-linked sub-page;
//  - VIEW_TIMESTAMPS each time the work-calendar ("Aikaleimat") page is entered.
//    Leaving the calendar for another page in the same project clears its marker,
//    so returning logs again; a refresh (no intervening non-calendar navigation)
//    re-fires on the same /calendar/ URL and does not re-log.
// Dedup state lives in sessionStorage (not a module variable) so a full page
// refresh — which reloads the same project — does NOT create duplicate logs.
// sessionStorage is per-tab and cleared when the tab closes, so a new session logs
// again. Guarded to real /project/ navigation (not programmatic selection).
const ACTIVITY_LOG_LAST_PROJECT_VIEW_KEY = 'activityLog:lastProjectViewId';
const ACTIVITY_LOG_LAST_TIMESTAMPS_KEY = 'activityLog:lastTimestampsId';

const readLoggedId = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeLoggedId = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // sessionStorage unavailable — fall back to logging (no dedup).
  }
};

const clearLoggedId = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    // sessionStorage unavailable — nothing to clear.
  }
};

const projectViewLogMiddleware = (storeApi) => (next) => (action) => {
  const result = next(action);

  if (action.type === 'project/setSelectedProjectId') {
    const projectId = action.payload;
    const pathname = window.location.pathname;

    if (projectId && pathname.startsWith('/project/')) {
      if (projectId !== readLoggedId(ACTIVITY_LOG_LAST_PROJECT_VIEW_KEY)) {
        writeLoggedId(ACTIVITY_LOG_LAST_PROJECT_VIEW_KEY, projectId);
        storeApi.dispatch(logProjectOpened({ projectId }));
      }

      if (pathname.includes('/calendar/')) {
        // Entering the work-calendar page: log unless we're already marked as on
        // it for this project (a refresh re-fires here with the marker intact).
        if (projectId !== readLoggedId(ACTIVITY_LOG_LAST_TIMESTAMPS_KEY)) {
          writeLoggedId(ACTIVITY_LOG_LAST_TIMESTAMPS_KEY, projectId);
          storeApi.dispatch(logTimestampsOpened({ projectId }));
        }
      } else {
        // Navigated to another page in the project: clear the marker so the next
        // visit to the calendar logs a fresh VIEW_TIMESTAMPS.
        clearLoggedId(ACTIVITY_LOG_LAST_TIMESTAMPS_KEY);
      }
    }
  }

  return result;
};

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(businessConfigurationMiddleware, projectViewLogMiddleware),
});

const persistor = persistStore(store);

/**
 * initialize language and theme
 * note: below component is vital for newest i18next to work
 * it'll crash without this because i18next backend promise
 * has some serious issues and will crash before getting lang files
 * wait for i18n init before showing rest of the app
 * newest i18next-react added hooks which are quite useful and worth this
 */
// How long the "Sign in failed" screen shows before redirecting to login.
const SIGN_IN_REDIRECT_DELAY_MS = 1500;

// Bootstrap retry policy. A brand-new user can hit a transient failure right
// after sign-in (e.g. the userSub GSI not yet consistent), which clears within
// a few seconds — so we retry silently (still showing the logo) a couple of
// times before giving up and sending the user to re-login.
const MAX_BOOTSTRAP_ATTEMPTS = 3; // initial + 2 retries
const BOOTSTRAP_RETRY_DELAY_MS = 3000;
// Per-attempt cap so a *hang* (a request that never responds, not just an
// error) also counts as a failed attempt and triggers a retry/redirect.
const BOOTSTRAP_ATTEMPT_TIMEOUT_MS = 8000;

// Shown when the bootstrap (SSO credentials / user load) fails — a caught async
// error ErrorBoundary cannot catch. The only remedy is to sign in again
// (expired session / failed sign-in), so we briefly show the robot + message
// over the blue + centered-logo `html` background, then redirect to the account
// login (which returns the user here afterwards).
function BootError() {
  // Usually i18n is already initialized when this shows (the failure is the SSO
  // credential fetch, not i18n). The English defaultValue covers the rare case
  // where i18n itself failed to load.
  const { t } = useTranslation('common');

  useEffect(() => {
    const timer = setTimeout(() => {
      // Most common cause on iOS is an expired/dropped SSO session. Sending the
      // user back through the account login re-issues it; the current URL is
      // preserved as the callback so any pending action (e.g. a project join)
      // continues afterwards.
      window.location.href =
        Config.SSO_LOGIN_URL + encodeURIComponent(window.location.href);
    }, SIGN_IN_REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <Icon path={mdiRobotDeadOutline} size={3} color="white" />
      <Typography variant="body1" style={{ color: 'white' }}>
        {t('error.sign_in_failed.title', 'Sign in failed')}
      </Typography>
    </div>
  );
}

function AppInit() {
  const dispatch = useDispatch();
  // Seed from i18n.isInitialized to avoid the race where i18n finishes
  // initializing before this component's effect attaches the listener
  // (e.g. cached locale files), which otherwise leaves the app stuck forever.
  const [isLanguageInit, setIsLanguageInit] = useState(i18n.isInitialized);
  const [bootError, setBootError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const isUserSettingsLoaded = useSelector(selectUserSettingsLoaded);
  const isUserLoaded = useSelector(selectUserLoaded);

  const userSetting = useSelector((state) => state.root.userSetting);

  const theme =
    userSetting && userSetting.theme ? userSetting.theme : 'classic';

  const providerTheme = useTheme(theme);

  // Tint Safari's collapsed toolbar (iOS 15+) to the theme's canvas color —
  // the html backgroundColor each theme sets in its CssBaseline override.
  // Until settings load, the static #1687af in index.html matches the splash.
  useEffect(() => {
    if (!isUserSettingsLoaded) return;
    const canvasColor =
      providerTheme.overrides?.MuiCssBaseline?.['@global']?.html
        ?.backgroundColor;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (canvasColor && meta) {
      meta.setAttribute('content', canvasColor);
    }
  }, [isUserSettingsLoaded, providerTheme]);

  // Run the bootstrap (load user + settings). On failure — whether a rejection
  // or a hang (caught by the per-attempt watchdog) — retry silently (keep
  // showing the logo) up to MAX_BOOTSTRAP_ATTEMPTS, which clears transient
  // post-sign-in races like userSub GSI lag. Only after retries are exhausted
  // do we render BootError, which redirects to re-login so nobody is stuck
  // forever. Re-runs whenever `attempt` increments.
  useEffect(() => {
    let cancelled = false;
    let handled = false;
    let i18nHandler;
    let watchdog;
    let retryTimer;

    // Treat this attempt as failed exactly once (from a rejection, an
    // exception, or the watchdog timeout); then retry or give up.
    const handleFailure = (reason) => {
      if (cancelled || handled) return;
      handled = true;
      clearTimeout(watchdog);

      if (attempt + 1 < MAX_BOOTSTRAP_ATTEMPTS) {
        console.warn(
          `Bootstrap attempt ${attempt + 1} failed (${reason}); retrying`
        );
        retryTimer = setTimeout(
          () => setAttempt((a) => a + 1),
          BOOTSTRAP_RETRY_DELAY_MS
        );
      } else {
        console.error(
          `Bootstrap failed after ${MAX_BOOTSTRAP_ATTEMPTS} attempts (${reason})`
        );
        setBootError(true);
      }
    };

    // Resolve once i18n is ready. Seeding isLanguageInit from
    // i18n.isInitialized also covers the race where init finished before this
    // effect attaches the listener.
    const waitForI18n = () =>
      i18n.isInitialized
        ? Promise.resolve()
        : new Promise((resolve) => {
            i18nHandler = resolve;
            i18n.on('initialized', resolve);
          });

    // Backstop: a request that never settles would otherwise hang forever.
    watchdog = setTimeout(
      () => handleFailure('timeout'),
      BOOTSTRAP_ATTEMPT_TIMEOUT_MS
    );

    const run = async () => {
      const userPromise = dispatch(loadUser());

      await waitForI18n();
      if (cancelled || handled) return;
      setIsLanguageInit(true);
      const settingsPromise = dispatch(loadUserSettings());

      const [userRes, settingsRes] = await Promise.all([
        userPromise,
        settingsPromise,
      ]);
      if (cancelled || handled) return;

      const error =
        (userRes && userRes.error) || (settingsRes && settingsRes.error);
      if (error) {
        handleFailure('error');
      } else {
        clearTimeout(watchdog);
      }
    };

    run().catch(() => handleFailure('exception'));

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      if (retryTimer) clearTimeout(retryTimer);
      if (i18nHandler) i18n.off('initialized', i18nHandler);
    };
  }, [dispatch, attempt]);

  if (bootError) {
    return <BootError />;
  }

  return isUserSettingsLoaded && isLanguageInit && isUserLoaded ? (
    <MuiThemeProvider theme={providerTheme}>
      <CssBaseline />
      <Router>
        <App />
      </Router>
    </MuiThemeProvider>
  ) : null;
}

// optional configuration for react-alert
const options = {
  // you can also just use 'bottom center'
  position: positions.TOP_CENTER,
  timeout: 0,
  offset: '30px',
  // you can also just use 'scale'
  transition: transitions.SCALE,
  type: 'error',
};

const renderApp = () =>
  ReactDOM.render(
    <ErrorBoundary>
      <AppSyncClient
        graphqlEndpoint={Config.GRAPHQL_ENDPOINT}
        awsRegion={Config.AWS_REGION}
        awsAuthType={Config.AWS_AUTH_TYPE}
        ssoTokenUrl={Config.SSO_TOKEN_URL}
        ssoLoginUrl={Config.SSO_LOGIN_URL}
        filesDomain={Config.FILES_DOMAIN}
        build={Config.BUILD_TS}
        mutationQueryResolver={mutationQueryResolver}
        graphqlErrorHandler={graphqlErrorHandler}
        loading={<></>}
      >
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <I18nextProvider i18n={i18n}>
              <AlertProvider template={AlertTemplate} {...options}>
                <SnackbarProvider maxSnack={5}>
                  <AppInit />
                </SnackbarProvider>
              </AlertProvider>
            </I18nextProvider>
          </PersistGate>
        </Provider>
      </AppSyncClient>
    </ErrorBoundary>,
    document.getElementById('root')
  );

const swOnInstall = () => {
  console.log('serviceworker update detected');
  // give 2 seconds to app to init, because listening in app.js
  setTimeout(() => window.postMessage({ type: 'swInstall' }), '2000');
};

serviceWorkerRegistration.register({ onInstall: swOnInstall });

if (localStorage.getItem('lixaniLibGraphqlBuild') === Config.BUILD_TS) {
  renderApp();
} else {
  renderApp();
}
